import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'

import type { BusinessCard, BusinessCardStatusType } from '@/api/admin-companies-types'
import { useUpdateBusinessCard } from '@/api/api-mutations'
import { useListCompanies, useListCustomers } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'
import { useDebounce } from '@/utils/hooks'

export const Route = createFileRoute('/_authenticated/_general/business-cards/$cardUuid/edit')({
	component: RouteComponent,
	staticData: {
		crumb: ({ context: { L } }) => L.BusinessCards.EditTitle()
	}
})

function RouteComponent() {
	const { cardUuid } = Route.useParams()
	return (
		<RouteModal asDrawer requiresData loader={async api => await api.getBusinessCard({ path: { cardUuid } })}>
			{data => <EditBusinessCardForm card={data} />}
		</RouteModal>
	)
}

function EditBusinessCardForm({ card }: { card: BusinessCard }) {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const { cardUuid } = Route.useParams()

	const updateCard = useUpdateBusinessCard()

	const [companySearch, setCompanySearch] = useState('')
	const debouncedCompanySearch = useDebounce(companySearch)
	const companies = useListCompanies(
		{ limit: 20, offset: 0, q: debouncedCompanySearch || undefined },
		{ enabled: debouncedCompanySearch.length > 0 }
	)

	const [customerSearch, setCustomerSearch] = useState('')
	const debouncedCustomerSearch = useDebounce(customerSearch)
	const customers = useListCustomers(
		{ limit: 20, offset: 0, q: debouncedCustomerSearch || undefined },
		{ enabled: debouncedCustomerSearch.length > 0 }
	)

	const companyOptions = useMemo(() => {
		const options: { value: string; label: string }[] = []
		if (card.company) {
			options.push({ value: card.company.uuid, label: card.company.name })
		}
		if (companies.data?.companies) {
			for (const c of companies.data.companies) {
				if (!options.some(o => o.value === c.uuid)) {
					options.push({ value: c.uuid, label: c.name })
				}
			}
		}
		return options
	}, [card.company, companies.data])

	const customerOptions = useMemo(() => {
		const options: { value: string; label: string }[] = []
		if (card.customer) {
			const name = [card.customer.firstName, card.customer.lastName].filter(Boolean).join(' ')
			options.push({ value: card.customer.uuid, label: name || card.customer.email || L.Customers.Anonymous() })
		}
		if (customers.data?.customers) {
			for (const c of customers.data.customers) {
				if (!options.some(o => o.value === c.uuid)) {
					const name = [c.firstName, c.lastName].filter(Boolean).join(' ')
					options.push({ value: c.uuid, label: name || c.email || L.Customers.Anonymous() })
				}
			}
		}
		return options
	}, [card.customer, customers.data, L])

	const form = useAppForm({
		defaultValues: {
			name: card.name,
			status: card.status as BusinessCardStatusType,
			externalId: card.externalId ?? '',
			companyUuid: card.company?.uuid ?? '',
			customerUuid: card.customer?.uuid ?? ''
		},
		onSubmit: async ({ value }) => {
			const result = await updateCard({
				path: { cardUuid },
				body: {
					name: value.name,
					status: value.status,
					externalId: value.externalId || undefined,
					companyUuid: value.companyUuid || undefined,
					customerUuid: value.customerUuid || null
				}
			})
			if (result.isSuccess) {
				toast.success(L.BusinessCards.EditSuccess())
				handleSuccess()
			} else {
				toast.error(L.Error.SomethingWentWrong())
			}
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.BusinessCards.EditTitle()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField
							name='name'
							validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
							children={field => <field.TextField autoFocus label={L.Fields.Name()} />}
						/>
						<form.AppField
							name='status'
							children={field => (
								<field.SelectField
									label={L.Fields.Status()}
									options={[
										{ value: 'ACTIVE', label: L.BusinessCards.Status('ACTIVE') },
										{ value: 'INACTIVE', label: L.BusinessCards.Status('INACTIVE') },
										{ value: 'BLOCKED', label: L.BusinessCards.Status('BLOCKED') }
									]}
								/>
							)}
						/>
						<form.AppField
							name='externalId'
							children={field => <field.TextField label={L.BusinessCards.Column.ExternalId()} optional />}
						/>
						<form.AppField
							name='companyUuid'
							children={field => (
								<field.SingleComboboxField
									label={L.BusinessCards.Detail.Company()}
									options={companyOptions}
									onSearchValueChange={setCompanySearch}
									optional
								/>
							)}
						/>
						<form.AppField
							name='customerUuid'
							children={field => (
								<field.SingleComboboxField
									label={L.BusinessCards.Detail.Customer()}
									options={customerOptions}
									onSearchValueChange={setCustomerSearch}
									optional
								/>
							)}
						/>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.SaveButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
